import { getEvmClient } from '@1delta/providers'
import { getCompoundV2Tokens } from '../addresses/compoundV2'

/**
 * What ONE unit of a Compound V2 market's `*RatePerBlock` actually measures.
 *
 * The name lies. `borrowRatePerBlock` is whatever the fork's `accrueInterest`
 * multiplies by, and across the forks we serve that is three different clocks:
 *
 *  - `timestamp`  — the fork counts SECONDS (`block.timestamp`). Sonne on Base
 *                   and Iron Bank on Optimism do this while still calling the
 *                   getter `...PerBlock`.
 *  - `block`      — a real block counter. One unit is one block, so the annual
 *                   cost moves with the CHAIN's block cadence, not with the
 *                   rate.
 *  - `l1-block`   — Arbitrum. Contracts there see an L1 block number, so one
 *                   unit is ~12 s no matter how fast the L2 runs (0.25 s today).
 *
 * A per-chain seconds-per-block table cannot express this, and got it wrong in
 * both directions (measured 2026-08-24):
 *
 *   DEMETER/56    table 0.75 s, real 0.450 s  → published 576 % where the
 *                 market charges 960 % (BSC went 3 s → 0.75 s → 0.45 s and the
 *                 fork was never redeployed)
 *   CREAM/42161   table 1 s, real ~12 s (L1)  → published 74.36 % where the
 *                 market charges 6.20 %
 *   SONNE/8453    table 1 s, clock is SECONDS → right by coincidence, since
 *                 per-second needs a divisor of exactly 1
 *
 * `accrualBlockNumber()` answers it outright and cannot go stale: compare it to
 * the chain's head block and head timestamp and the clock names itself.
 *
 * Two rules this resolver keeps:
 *
 *  1. **The block cadence is MEASURED, never tabled.** That is the number that
 *     drifted; a constant here would just reintroduce the bug on the next
 *     upgrade.
 *  2. **An unresolved probe changes nothing.** Every failure path returns
 *     `undefined` and the caller keeps the legacy per-chain table. Publishing a
 *     guessed rate is worse than publishing the old one, because the old one is
 *     at least the number every consumer has been reconciling against.
 */
export type RateClock = 'timestamp' | 'block' | 'l1-block'

/** Seconds per L1 block — the unit Arbitrum contracts count in. */
const L1_BLOCK_SECONDS = 12

/**
 * A known (block, timestamp) pair on Ethereum, used to estimate the current L1
 * height from any chain's wall clock — the L1 height is not readable from an L2.
 *
 * Anchored at the Merge rather than at genesis: proof-of-work block times
 * averaged ~13.5 s and drifted with difficulty, so extrapolating from block 0
 * at 12 s overshoots by millions of blocks. After the Merge the slot time is
 * exactly 12 s, which makes this arithmetic rather than a guess (empty slots
 * make it a slight over-estimate, ~0.3 % measured).
 */
const L1_MERGE_BLOCK = 15_537_394
const L1_MERGE_TIMESTAMP = 1_663_224_162

const estimateL1Height = (nowSeconds: number): number =>
  L1_MERGE_BLOCK + (nowSeconds - L1_MERGE_TIMESTAMP) / L1_BLOCK_SECONDS

/** Blocks to look back when measuring a chain's cadence. Long enough that a
 *  single slow block cannot move the average, short enough to stay in any
 *  node's recent history. */
const CADENCE_SPAN = 20_000n

/** Re-probe daily: the clock KIND never changes, but a chain's cadence does. */
const TTL_MS = 24 * 60 * 60 * 1000

type Cached<T> = { at: number; value: T }
const clockCache = new Map<string, Cached<RateClock | undefined>>()
const cadenceCache = new Map<string, Cached<number | undefined>>()

const fresh = <T>(e: Cached<T> | undefined): e is Cached<T> =>
  !!e && Date.now() - e.at < TTL_MS

/** Test seam — the caches are module-global, so suites must not inherit them. */
export function resetRateClockCache(): void {
  clockCache.clear()
  cadenceCache.clear()
}

/**
 * Which clock does `accrualBlockNumber` speak?
 *
 * The three candidates are orders of magnitude apart — a unix timestamp is
 * ~1.79e9, a BNB block height ~1.18e8, an L1 height ~2.6e7 — so "closest match"
 * is unambiguous. Compared in RATIO rather than absolute distance, because the
 * candidates differ by factors, not by offsets, and a market that has not
 * accrued for a while must still classify correctly.
 */
export function classifyRateClock(
  accrualBlockNumber: bigint,
  headBlockNumber: bigint,
  headTimestamp: bigint,
): RateClock | undefined {
  const v = Number(accrualBlockNumber)
  if (!Number.isFinite(v) || v <= 0) return undefined

  const candidates: [RateClock, number][] = [
    ['timestamp', Number(headTimestamp)],
    ['block', Number(headBlockNumber)],
    // The L1 height is not readable from this chain; derive it from the L2
    // head's timestamp, which is the same wall clock Ethereum runs on.
    ['l1-block', estimateL1Height(Number(headTimestamp))],
  ]

  let best: RateClock | undefined
  let bestRatio = Infinity
  for (const [kind, expected] of candidates) {
    if (!Number.isFinite(expected) || expected <= 0) continue
    const ratio = v > expected ? v / expected : expected / v
    if (ratio < bestRatio) {
      bestRatio = ratio
      best = kind
    }
  }
  // A market can be stale, but not by an order of magnitude. Anything further
  // out than 2x from every candidate is a shape we do not recognise; decline.
  return bestRatio <= 2 ? best : undefined
}

/** Measured seconds per block for a chain. `undefined` when it cannot be read. */
async function chainBlockSeconds(chainId: string): Promise<number | undefined> {
  const hit = cadenceCache.get(chainId)
  if (fresh(hit)) return hit.value

  let value: number | undefined
  try {
    const client: any = await getEvmClient(chainId as any)
    const head = await client.getBlockNumber()
    const span = head > CADENCE_SPAN ? CADENCE_SPAN : head / 2n
    const [b1, b0] = await Promise.all([
      client.getBlock({ blockNumber: head }),
      client.getBlock({ blockNumber: head - span }),
    ])
    const secs = Number(b1.timestamp - b0.timestamp) / Number(span)
    // Sanity band: no chain we serve produces a block faster than 50 ms or
    // slower than a minute. Outside it, something answered with a shape we did
    // not ask for — decline rather than annualize off it.
    if (secs >= 0.05 && secs <= 60) value = secs
  } catch {
    value = undefined
  }
  cadenceCache.set(chainId, { at: Date.now(), value })
  return value
}

/**
 * Seconds represented by one unit of this fork's `*RatePerBlock`, or
 * `undefined` when it could not be established (caller keeps its fallback).
 *
 * Probed on ONE market per (lender, chain): the clock is a property of the
 * cToken implementation, which every market of a fork shares.
 */
export async function resolveRateUnitSeconds(
  chainId: string,
  lender: string,
): Promise<number | undefined> {
  const key = `${chainId}:${lender}`
  let clock = clockCache.get(key)
  if (!fresh(clock)) {
    let value: RateClock | undefined
    try {
      const sample = getCompoundV2Tokens(chainId, lender)[0]?.cToken
      if (sample) {
        const client: any = await getEvmClient(chainId as any)
        const head = await client.getBlock()
        const accrual: bigint = await client.readContract({
          address: sample as `0x${string}`,
          abi: [
            {
              inputs: [],
              name: 'accrualBlockNumber',
              outputs: [{ name: '', type: 'uint256' }],
              stateMutability: 'view',
              type: 'function',
            },
          ] as const,
          functionName: 'accrualBlockNumber',
        })
        value = classifyRateClock(accrual, head.number, head.timestamp)
      }
    } catch {
      value = undefined
    }
    clock = { at: Date.now(), value }
    clockCache.set(key, clock)
  }

  switch (clock.value) {
    case 'timestamp':
      return 1
    case 'l1-block':
      return L1_BLOCK_SECONDS
    case 'block':
      return await chainBlockSeconds(chainId)
    default:
      return undefined
  }
}
